import { useEffect } from 'react'
import { ConnectedInstance, useAuth, useBreakpoints, useUpdateEffect, useView } from '@10000/react'
import { Stack, useStack } from '../layout'
import { Exited, InProgress, Lobby, Participants, Profile } from './setup'
import { PracticeWheel, Toolbar } from './common'
import { Analytics, Display, Waiting } from './display'
import { Confirmation, Register, RegisterSplash } from '../kiosk'
import { Consensus, Question, Results, TeamAverage } from './participant'
import { Dashboard } from '../analytics'
import { Recap, Splash as RecapSplash } from './recap'
import { Tutorial } from './setup'
import { FreeButtonBar } from '../free'
import { query } from '../../utilities.js'
import { preload } from './preload.js'

export function Host({ instanceId, region, view, active }) {
  return <ConnectedInstance
    region={region}
    id={instanceId}
    viewId={view}
    entity="squiz"
    aggregates={['squiz']}
    topic="squizInput"
    robustClientId={!view}
  >
    <StackWrapper view={view} active={active} />
  </ConnectedInstance>
}

const StackWrapper = ({ view, active }) => {
  const { instanceId } = query()
  const data = useView('squiz')
  const { teamAverages, options: { kiosk, consensus } = {} } = data
  const firstQuestion = teamAverages?.every(x => x === null)
  const { authenticated } = useAuth()
  const { mobile } = useBreakpoints()

  const displayMode = view === 'display' || (kiosk && authenticated && !instanceId)

  useEffect(() => {
    if(consensus !== undefined) {
      preload(consensus)
    }
  }, [consensus])

  return (
    <Stack
      initialStack={displayMode ? [{ Component: Display }] : [{ Component: getComponent({ ...data, firstQuestion }, true) }]}
      limitHeight={!displayMode}
      Wrapper={PracticeWheel}
      WrapperProps={{ displayMode }}
      active={active}
    >
      <Toolbar />
      {!mobile && <FreeButtonBar displayMode={displayMode} />}
      {!displayMode && <Controller />}
    </Stack>
  )
}

const Controller = () => {
  const { presenter, stage, result, questionIndex, joined, canJoin, registered } = useView('squiz')
  const { replaceStack } = useStack()

  useUpdateEffect(() => {
    if(!result || result.startsWith('teamAverage') || stage === 'analytics' || stage === 'exited') { // flaky as fuck and duplicated in Display
      const component = getComponent({ presenter, stage, questionIndex, joined, canJoin, registered })
      if(component) {
        replaceStack(component)
      }
    }
  }, [replaceStack, presenter, stage, result, questionIndex, joined, canJoin, registered])

  return null
}

function getComponent({ presenter, stage, questionIndex, joined, canJoin, registered }, initial = false) {
  if (stage === 'recap') {
    return initial ? Recap : RecapSplash
  } else if (stage === 'register') {
    return presenter ? registered ? Confirmation : Register : RegisterSplash
  } else if (stage === 'analytics') {
    return presenter ? Dashboard : Analytics
  } else if (stage === 'consensus') {
    return Consensus
  } else if (stage === 'question-option' && presenter) {
    return Question
  } else if (stage === 'teamAverage') {
    return TeamAverage
  } else if (stage === 'results') {
    return Results
  } else if (stage === 'question-option') {
    return Waiting
  } else if (stage === 'tutorial' || stage === 'consensus-tutorial' || stage === 'results-tutorial') {
    return Tutorial
  } else if (stage === 'exited') {
    return presenter ? undefined : Exited
  } else if (questionIndex === -1) {
    return presenter ? Participants : joined ? Lobby : Profile
  } else {
    return canJoin ? Profile : InProgress
  }
}

